import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Card, Typography, Alert, Box, Avatar } from "@mui/material";
import { FaIdCard, FaUpload } from "react-icons/fa";
import { toast } from 'react-toastify';
import COLORS from "../../../../../constants/colors";
import baseURL from "../../config";

// NationalIDUpload now accepts 'idImages' and 'onDataUpdated' as props
const NationalIDUpload = ({ idImages, onDataUpdated }) => {
  const [error, setError] = useState(null);

  const handleUpload = async (file, side) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("side", side);

    try {
      const response = await fetch(`${baseURL}/user/national-id/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to upload image");

      toast.success("National ID uploaded successfully!");
      if (onDataUpdated) {
        onDataUpdated(); // Trigger parent to re-fetch data
      }
    } catch (error) {
      setError("Error uploading image. Please try again.");
      console.error("Upload error:", error);
      toast.error("Failed to upload image.");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onDropFront = useCallback((acceptedFiles) => {
    handleUpload(acceptedFiles[0], "front");
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onDropBack = useCallback((acceptedFiles) => {
    handleUpload(acceptedFiles[0], "back");
  }, []);

  const { getRootProps: getFrontRootProps, getInputProps: getFrontInputProps } = useDropzone({
    onDrop: onDropFront,
    accept: ".png, .jpeg, .jpg",
    multiple: false,
  });

  const { getRootProps: getBackRootProps, getInputProps: getBackInputProps } = useDropzone({
    onDrop: onDropBack,
    accept: ".png, .jpeg, .jpg",
    multiple: false,
  });

  return (
    <Card
      sx={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        border: "1px solid rgba(0,0,0,0.05)",
        p: 3,
        textAlign: "left",
        mb: 4,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 800,
          color: "#1a1a2e",
          fontSize: "1.05rem",
          letterSpacing: "-0.3px",
          mb: 2,
          display: "flex",
          alignItems: "center",
        }}
      >
        <FaIdCard style={{ marginRight: 8, color: "#2D4A3D" }} />
        National ID Upload (Mandatory)
      </Typography>

      <Alert
        severity="info"
        sx={{
          mb: 3,
          fontSize: "0.85rem",
          borderRadius: 2,
        }}
      >
        <strong>Important:</strong> Uploading both front and back photos of your
        National ID is required for your account to be approved.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Upload Area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 3,
        }}
      >
        {/* Front ID Upload */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: { xs: "100%", sm: "50%" },
          }}
        >
          <Box {...getFrontRootProps()} sx={dropZoneStyle}>
            <input {...getFrontInputProps()} />
            {idImages.front ? (
              <Avatar src={idImages.front} sx={imageStyle} variant="rounded" />
            ) : (
              <Typography sx={uploadTextStyle}>
                <FaUpload style={{ marginRight: 8 }} /> Drag & Drop or Click to
                Upload Front ID
              </Typography>
            )}
          </Box>
          <Typography sx={labelStyle}>Front of ID</Typography>
        </Box>

        {/* Back ID Upload */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: { xs: "100%", sm: "50%" },
          }}
        >
          <Box {...getBackRootProps()} sx={dropZoneStyle}>
            <input {...getBackInputProps()} />
            {idImages.back ? (
              <Avatar src={idImages.back} sx={imageStyle} variant="rounded" />
            ) : (
              <Typography sx={uploadTextStyle}>
                <FaUpload style={{ marginRight: 8 }} /> Drag & Drop or Click to
                Upload Back ID
              </Typography>
            )}
          </Box>
          <Typography sx={labelStyle}>Back of ID</Typography>
        </Box>
      </Box>
    </Card>
  );
};

const dropZoneStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: 120,
  border: `2px dashed ${COLORS.primary}`,
  borderRadius: "8px",
  cursor: "pointer",
  backgroundColor: "#f9f9f9",
  transition: "background-color 0.3s",
  "&:hover": {
    backgroundColor: "#eaeaea",
  },
};

const uploadTextStyle = {
  fontSize: "0.85rem",
  textAlign: "center",
  color: COLORS.primary,
  display: "flex",
  alignItems: "center",
};

const imageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "8px",
};

const labelStyle = {
  fontSize: "0.75rem",
  textAlign: "center",
  color: COLORS.primary,
  marginTop: 2,
};

export default NationalIDUpload;